/**
 * Versioned asset URLs (#285). A file's version is the start of its content
 * hash, so it changes exactly when its bytes do. `/assets/<path>?v=<version>`
 * is served immutable (cached for a year with no revalidation), so a client
 * that has a copy never asks for it again; a changed file gets a new
 * version, so a new URL. Without `v`, a file is served as before: cached,
 * but revalidated on every use.
 */

/** How many hex digits of the content hash a version keeps: plenty to tell two copies of a file apart. */
const VERSION_LENGTH = 16;

/** A file's version from its content hash (`sha256-<hex>`, as content-hash.ts and the catalogs write it). */
export function toAssetVersion(contentHash: string): string {
    return contentHash.replace(/^sha256-/, '').slice(0, VERSION_LENGTH);
}

/** The URL with its version: `?v=` added, or `&v=` after a query it already has. */
export function withAssetVersion(url: string, version: string): string {
    return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
}

/** What a precache tier is for: `core` before the player enters Sorrem, `run` in the background after. */
export type AssetPrecacheTier = 'core' | 'run';

/** How a manifest entry is fetched into the client's caches. */
export type AssetManifestModelKind = 'character' | 'equipment' | 'body-part' | 'animation';

/** A catalog model a manifest file is: its kind and its catalog id. */
export interface AssetManifestModel {
    readonly kind: AssetManifestModelKind;
    readonly id: string;
}

/** One file in the asset manifest (`/assets/manifest`). */
export interface AssetManifestEntry {
    /** Its path under `/assets/`. */
    readonly path: string;
    /** Its content hash (`sha256-<hex>`); its URL version is `toAssetVersion` of it, and a model's cached copy is checked against it. */
    readonly hash: string;
    readonly sizeBytes: number;
    /** When the client fetches it ahead of use; absent: on first use only. */
    readonly tier?: AssetPrecacheTier;
    /**
     * The catalog models this file is (several ids can share one file),
     * fetched by catalog id into the client's model cache (IndexedDB)
     * rather than by its path.
     */
    readonly models?: readonly AssetManifestModel[];
}

/** `/assets/manifest`: every file under `/assets/`, with its version and when to precache it. */
export interface AssetManifest {
    readonly version: number;
    readonly entries: readonly AssetManifestEntry[];
}
